/**
 * Shared hook for fetching and managing document preview content
 * Used by both hover preview and references sidebar systems
 *
 * ✅ Implements in-memory caching to prevent redundant API calls
 * when sidebar is collapsed/expanded or components remount
 */

import { CacheManager } from '@/lib/cache';
import {
  parseFileIdHref,
  parseStorageNodeHref,
} from '@/lib/utils/markdownUtils';
import documentServices from '@/services/document-services';
import getLegislationHtmlServices from '@/services/get-legislation-html-services';
import storageService from '@/services/storage-service';
import userFilesServices from '@/services/user-files-services';
import {
  type ExternalUrlInfo,
  type ParsedDocumentUrl,
  decompressHtml,
  extractExternalUrlInfo,
  getDocumentType,
  isExternalUrl,
  parseDocumentUrl,
} from '@/utils/previewUtils';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

// Forward declaration for DocumentContent (defined below)
// CacheManager is initialized after interface definition

/**
 * Request queue to prevent overwhelming the backend with simultaneous requests
 * Limits concurrent document fetches to MAX_CONCURRENT_REQUESTS
 *
 * Higher limit (20) for high-priority requests, lower (3) for normal priority.
 * This ensures hover previews always have slots available.
 *
 * Priority queue system:
 * - High priority queue (hover previews) - up to 20 concurrent requests
 * - Normal priority queue (sidebar tiles) - limited to 3 concurrent requests
 */
const MAX_HIGH_PRIORITY_REQUESTS = 20;
const MAX_NORMAL_PRIORITY_REQUESTS = 3;
let activeHighPriorityRequests = 0;
let activeNormalPriorityRequests = 0;
const highPriorityQueue: Array<() => Promise<void>> = []; // Hover previews
const normalPriorityQueue: Array<() => Promise<void>> = []; // Sidebar tiles

/**
 * Process the next request in the queue
 * Prioritizes high priority requests (hover previews) over normal priority (sidebar)
 * High priority gets more concurrent slots to ensure instant response
 */
function processQueue() {
  // Try to process high priority requests first (up to 20 concurrent)
  if (
    highPriorityQueue.length > 0 &&
    activeHighPriorityRequests < MAX_HIGH_PRIORITY_REQUESTS
  ) {
    const nextRequest = highPriorityQueue.shift();
    if (nextRequest) {
      activeHighPriorityRequests++;
      nextRequest()
        .finally(() => {
          activeHighPriorityRequests--;
          processQueue(); // Process next queued request
        })
        .catch((err: Error) => {
          console.error(
            '[usePreviewContent] High priority request failed:',
            err
          );
        });
    }
  }

  // Process normal priority requests (limited to 3 concurrent to save bandwidth for hovers)
  if (
    normalPriorityQueue.length > 0 &&
    activeNormalPriorityRequests < MAX_NORMAL_PRIORITY_REQUESTS
  ) {
    const nextRequest = normalPriorityQueue.shift();
    if (nextRequest) {
      activeNormalPriorityRequests++;
      nextRequest()
        .finally(() => {
          activeNormalPriorityRequests--;
          processQueue(); // Process next queued request
        })
        .catch((err: Error) => {
          console.error(
            '[usePreviewContent] Normal priority request failed:',
            err
          );
        });
    }
  }
}

/**
 * Queue a fetch request to prevent overwhelming the backend
 * Includes automatic retry logic for transient network failures
 * @param fetchFn - The fetch function to queue
 * @param priority - 'high' for hover previews (processed first), 'normal' for sidebar tiles
 * @returns Promise that resolves when the fetch completes
 */
function queueRequest<T>(
  fetchFn: () => Promise<T>,
  priority: 'high' | 'normal' = 'normal'
): Promise<T> {
  return new Promise((resolve, reject) => {
    const wrappedFetch = async () => {
      const MAX_RETRIES = 2;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const result = await fetchFn();
          resolve(result);
          return;
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));

          // Don't retry on last attempt
          if (attempt < MAX_RETRIES) {
            // Exponential backoff: 500ms, 1000ms
            const delay = 500 * (attempt + 1);
            console.warn(
              `[usePreviewContent] Retry ${attempt + 1}/${MAX_RETRIES} after ${delay}ms...`
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      // All retries failed
      reject(lastError);
    };

    // Add to appropriate queue based on priority
    if (priority === 'high') {
      highPriorityQueue.push(wrappedFetch);
    } else {
      normalPriorityQueue.push(wrappedFetch);
    }

    processQueue();
  });
}

/**
 * Document content structure
 */
interface DocumentContent {
  /** Main HTML content */
  htmlContent: string | null;
  /** Additional sudHtml content for decision documents */
  sudHtmlContent: string | null;
  /** PDF URL if document is PDF-based */
  pdfUrl: string | null;
  /** Image URL if document is image-based */
  imageUrl: string | null;
  /** Document title */
  documentTitle: string | null;
  /** IRI for the document (may be null for ID-based documents) */
  iri: string | null;
  /** Original source URL from backend API (external slov-lex.sk link) */
  originalSource: string | null;
}

/**
 * In-memory cache for document content
 * Key: `${href}:${locale}` (composite key)
 * Value: DocumentContent
 *
 * Cache is cleared when user navigates away or refreshes page
 * No TTL - documents are static content
 */
const documentContentCache = new CacheManager<DocumentContent>(
  'PreviewContent',
  0 // No TTL - document content is static
);

/**
 * Hook state
 */
interface UsePreviewContentReturn {
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Fetched document content */
  content: DocumentContent;
  /** Parsed URL components */
  parsed: ParsedDocumentUrl;
  /** Section ID from URL hash */
  sectionId: string | null;
  /** Combined HTML content (main + sudHtml) */
  combinedHtmlContent: string | null;
  /** Whether the URL is an external link (not an internal document) */
  isExternal: boolean;
  /** External URL info (only set when isExternal is true) */
  externalUrlInfo: ExternalUrlInfo | null;
}

/**
 * Custom hook to fetch and manage document preview content
 *
 * Handles:
 * - Legislation documents (IRI-based) via getLegislationHtmlServices
 * - Decision documents (ID-based) via documentServices
 * - PDF documents
 * - HTML decompression
 * - Section ID extraction from URL hash
 * - Combined HTML rendering (main + sudHtml for decisions)
 *
 * @param href - Full URL to document
 * @param locale - Current locale (for translations)
 * @param priority - 'high' for hover previews (processed first), 'normal' for sidebar tiles (default)
 * @returns Loading state, error, content, and parsed URL data
 *
 * @example
 * const { loading, error, content, sectionId, combinedHtmlContent } = usePreviewContent(
 *   "https://lexomat.sk/sk/document/12345#section",
 *   "sk",
 *   "high" // For hover previews
 * );
 */
export function usePreviewContent(
  href: string,
  locale: string,
  priority: 'high' | 'normal' = 'normal'
): UsePreviewContentReturn {
  const t = useTranslations('TextPreview');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<DocumentContent>({
    htmlContent: null,
    sudHtmlContent: null,
    pdfUrl: null,
    imageUrl: null,
    documentTitle: null,
    iri: null,
    originalSource: null,
  });

  // Parse URL once on mount or when href changes
  const parsed = parseDocumentUrl(href);
  const sectionId = parsed.sectionId;

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Create cache key from href and locale
        const cacheKey = `${href}:${locale}`;

        // Check if content is already cached
        const cachedContent = documentContentCache.get(cacheKey);
        if (cachedContent) {
          setContent(cachedContent);
          setLoading(false);
          return;
        }

        // Reset content
        setContent({
          htmlContent: null,
          sudHtmlContent: null,
          pdfUrl: null,
          imageUrl: null,
          documentTitle: null,
          iri: parsed.iri,
          originalSource: null,
        });

        const parsedFileId = parseFileIdHref(href);
        if (parsedFileId) {
          await queueRequest(
            () =>
              fetchUserFile(
                parsedFileId.fileId,
                parsedFileId.fragment,
                cacheKey
              ),
            priority
          );
          return;
        }

        const parsedStorageNode = parseStorageNodeHref(href);
        if (parsedStorageNode) {
          await queueRequest(
            () =>
              fetchStorageNode(
                parsedStorageNode.nodeId,
                parsedStorageNode.fragment,
                cacheKey
              ),
            priority
          );
          return;
        }

        const docType = getDocumentType(parsed);

        if (docType === 'decision' && parsed.documentId) {
          // Decision document - fetch via document ID (queued to prevent overload)
          // Pass priority to ensure hover previews are processed first
          await queueRequest(
            () => fetchDecisionDocument(parsed.documentId!, cacheKey),
            priority
          );
        } else if (docType === 'legislation' && parsed.iri) {
          // Legislation document - fetch via IRI (queued to prevent overload)
          // Pass priority to ensure hover previews are processed first
          await queueRequest(
            () => fetchLegislationDocument(parsed.iri!, cacheKey),
            priority
          );
        } else {
          setError(t('cannotExtractPreview'));
        }
      } catch (err) {
        console.error('[usePreviewContent] Error fetching content:', err);
        setError(err instanceof Error ? err.message : t('failedToLoadPreview'));
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [href, locale]);

  /**
   * Fetch decision document by ID
   */
  const fetchDecisionDocument = async (docId: number, cacheKey: string) => {
    try {
      // Fetch document info including URLs
      const response = await documentServices.getDocumentInfo(docId);
      const docData = response.data?.data;

      // Extract document title and original source URL
      const title =
        docData?.document_title ||
        docData?.case_file_number ||
        docData?.legislation_regulation_number ||
        null;
      const originalSource = docData?.original_source || null;

      // Get URLs
      const htmlUrl =
        docData?.urls?.html_url || docData?.urls?.html_secHtml_url;
      const pdfUrlFromData = docData?.urls?.pdf_url;
      const fileType = docData?.file_type;

      // Handle PDF documents
      if (fileType === 'PDF' || (pdfUrlFromData && !htmlUrl)) {
        if (pdfUrlFromData) {
          const pdfContent = {
            htmlContent: null,
            sudHtmlContent: null,
            pdfUrl: pdfUrlFromData,
            imageUrl: null,
            documentTitle: title,
            iri: docData?.iri || null,
            originalSource,
          };
          setContent(pdfContent);
          // Cache the content
          documentContentCache.set(cacheKey, pdfContent);
        } else {
          setError(t('noPdfAvailable'));
        }
        return;
      }

      // Handle HTML documents
      if (htmlUrl) {
        const response = await fetch(htmlUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch HTML`);
        }

        const rawHtml = await decompressHtml(response);
        const html = getLegislationHtmlServices.sanitizeSlovLexHtml(rawHtml);

        // Fetch sudHtml if available (for decision documents)
        let sudHtml: string | null = null;
        const sudHtmlUrl = docData?.urls?.html_sudHtml_url;
        if (sudHtmlUrl) {
          try {
            const sudResponse = await fetch(sudHtmlUrl);
            if (sudResponse.ok) {
              sudHtml = await decompressHtml(sudResponse);
            }
          } catch (sudErr) {
            console.warn('[usePreviewContent] Error fetching sudHtml:', sudErr);
            // Continue without sudHtml - it's optional
          }
        }

        const htmlContent = {
          htmlContent: html,
          sudHtmlContent: sudHtml,
          pdfUrl: null,
          imageUrl: null,
          documentTitle: title,
          iri: docData?.iri || null,
          originalSource,
        };
        setContent(htmlContent);
        // Cache the content
        documentContentCache.set(cacheKey, htmlContent);
        return;
      }

      // No content available
      setError(t('noContentAvailable'));
    } catch (err) {
      console.error(
        '[usePreviewContent] Error fetching decision document:',
        err
      );
      throw err;
    }
  };

  /**
   * Fetch user-uploaded file by user_files.id (canonical file_id:<id> reference).
   */
  const fetchUserFile = async (
    fileId: number,
    fragment: string,
    cacheKey: string
  ) => {
    try {
      const response = await userFilesServices.getFile(fileId);
      const file = response.data;

      if (!file?.download_url) {
        throw new Error(t('noPdfAvailable'));
      }

      const pdfUrlWithFragment = fragment
        ? `${file.download_url}${fragment}`
        : file.download_url;
      const mimeType = file.mime_type || '';
      const isImage = mimeType.startsWith('image/');

      const userFileContent = {
        htmlContent: null,
        sudHtmlContent: null,
        pdfUrl: isImage ? null : pdfUrlWithFragment,
        imageUrl: isImage ? pdfUrlWithFragment : null,
        documentTitle: file.original_filename || `File ${fileId}`,
        iri: null,
        originalSource: null,
      };
      setContent(userFileContent);
      documentContentCache.set(cacheKey, userFileContent);
    } catch (err) {
      console.error('[usePreviewContent] Error fetching user file:', err);
      const status = (err as { response?: { status?: number } }).response
        ?.status;
      if (status === 403 || status === 404) {
        throw new Error(t('noPdfAvailable'));
      }
      throw new Error(t('failedToLoadPreview'));
    }
  };

  /**
   * Fetch client file (storage_node) by storage_nodes.id (client-file citation
   * scheme storage_node:<id>). Mirrors fetchUserFile above.
   */
  const fetchStorageNode = async (
    nodeId: number,
    fragment: string,
    cacheKey: string
  ) => {
    try {
      const response = await storageService.getNode(nodeId);
      const node = response.data;

      if (!node?.download_url) {
        throw new Error(t('noPdfAvailable'));
      }

      const pdfUrlWithFragment = fragment
        ? `${node.download_url}${fragment}`
        : node.download_url;
      const contentType = node.content_type || '';
      const isImage = contentType.startsWith('image/');

      const storageNodeContent = {
        htmlContent: null,
        sudHtmlContent: null,
        pdfUrl: isImage ? null : pdfUrlWithFragment,
        imageUrl: isImage ? pdfUrlWithFragment : null,
        documentTitle: node.name || `File ${nodeId}`,
        iri: null,
        originalSource: null,
      };
      setContent(storageNodeContent);
      documentContentCache.set(cacheKey, storageNodeContent);
    } catch (err) {
      console.error('[usePreviewContent] Error fetching storage node:', err);
      const status = (err as { response?: { status?: number } }).response
        ?.status;
      if (status === 403 || status === 404) {
        throw new Error(t('noPdfAvailable'));
      }
      throw new Error(t('failedToLoadPreview'));
    }
  };

  /**
   * Fetch legislation document by IRI
   */
  const fetchLegislationDocument = async (iri: string, cacheKey: string) => {
    try {
      // Fetch HTML content from Supabase storage
      const html = await getLegislationHtmlServices.getLegislationHTML(iri);

      // Also fetch metadata from document API
      let title: string | null = null;
      let originalSource: string | null = null;
      try {
        // Call API with IRI (without leading slash): SK/ZZ/1964/40
        const normalizedIri = iri.startsWith('/') ? iri.slice(1) : iri;
        const response = await documentServices.getDocumentInfo(normalizedIri);
        const docData = response.data?.data;
        title = docData?.document_title || null;
        originalSource = docData?.original_source || null;
      } catch (metaErr) {
        console.warn(
          '[usePreviewContent] Could not fetch legislation metadata:',
          metaErr
        );
        // Continue without metadata - HTML is the main content
      }

      const legislationContent = {
        htmlContent: html,
        sudHtmlContent: null,
        pdfUrl: null,
        imageUrl: null,
        documentTitle: title,
        iri,
        originalSource,
      };
      setContent(legislationContent);
      // Cache the content
      documentContentCache.set(cacheKey, legislationContent);
    } catch (err) {
      console.error(
        '[usePreviewContent] Error fetching legislation document:',
        err
      );
      throw err;
    }
  };

  // Combine HTML and sudHtml content for rendering
  const combinedHtmlContent =
    content.htmlContent && content.sudHtmlContent
      ? `<div><div class="html-content">${content.htmlContent}</div><div class="sud-html-content">${content.sudHtmlContent}</div></div>`
      : content.htmlContent;

  // Check if this is an external URL and extract info
  const isExternal = isExternalUrl(href);
  const externalUrlInfo = isExternal ? extractExternalUrlInfo(href) : null;

  return {
    loading,
    error,
    content,
    parsed,
    sectionId,
    combinedHtmlContent,
    isExternal,
    externalUrlInfo,
  };
}
